import { useEffect, useRef, useState } from "react";
import { X, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "exit_intent_dismissed_v1";
const VISITS_KEY = "exit_intent_visits_v1";
// Cooldown after dismiss — don't re-show for a week
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
// Only start showing after the visitor has landed 3+ times — respect first impressions.
const MIN_VISITS_BEFORE_SHOWING = 3;

/**
 * ExitIntentModal — captures emails from visitors about to leave.
 *
 * Trigger conditions (any one):
 *   1. Desktop: mouse moves toward the browser chrome (top of viewport) after
 *      the user has been on the page for at least 6 seconds.
 *   2. Mobile: rapid upward scroll after any downward engagement — proxy for
 *      "about to bounce" since there's no cursor-out event.
 *
 * Never shows if:
 *   - User is already signed in
 *   - Modal was dismissed in the last 7 days
 *   - User hasn't been on page long enough (≥6s)
 */
export default function ExitIntentModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const armedAt = useRef(0);
  const engaged = useRef(false); // did user scroll down at any point?

  useEffect(() => {
    if (user) return; // signed-in users don't need the pitch
    try {
      const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (last && Date.now() - last < COOLDOWN_MS) return;
      // Visit counter — bump on every mount, but only fire once we've seen the
      // visitor return enough times that the pitch feels invited, not intrusive.
      const visits = Number(localStorage.getItem(VISITS_KEY) || 0) + 1;
      localStorage.setItem(VISITS_KEY, String(visits));
      if (visits < MIN_VISITS_BEFORE_SHOWING) return;
    } catch {
      /* localStorage unavailable — bail out silently */
      return;
    }

    armedAt.current = Date.now() + 6000; // arm after 6s on page
    let lastScrollY = window.scrollY;

    const fire = () => {
      if (Date.now() < armedAt.current) return;
      setOpen(true);
      cleanup();
    };

    const onMouseOut = (e) => {
      // Trigger when the cursor exits the top of the viewport (toward browser UI).
      // relatedTarget is null when leaving the window entirely.
      if (e.clientY <= 0 && !e.relatedTarget) fire();
    };

    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastScrollY + 40) engaged.current = true;
      // rapid upward scroll after engagement — likely bounce
      if (engaged.current && y < 30 && lastScrollY > 400) fire();
      lastScrollY = y;
    };

    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return cleanup;
  }, [user]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads/subscribe", { email, source: "exit-intent" });
      toast.success("You're in — check your inbox for today's picks 📬", { duration: 4000 });
      dismiss();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't subscribe — try again");
    } finally {
      setBusy(false);
    }
  };

  if (!open || user) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(45,42,50,0.55)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      data-testid="exit-intent-modal"
    >
      <div
        className="relative w-full max-w-[520px] bg-white border-2 border-[#2d2a32] rounded-2xl hard-shadow p-8"
        role="dialog"
        aria-labelledby="exit-intent-title"
      >
        <button
          onClick={dismiss}
          aria-label="Close popup"
          data-testid="exit-intent-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full border-2 border-[#2d2a32] bg-white hover:bg-[#ff6b6b] hover:text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-[10px] tracking-[0.35em] text-[#ff5b48] font-bold inline-flex items-center gap-2 mb-1">
          <Sparkles className="w-3 h-3" /> WAIT — DON&#39;T LEAVE YET
        </div>
        <h2
          id="exit-intent-title"
          className="serif text-3xl md:text-4xl leading-[1.05] text-[#2d2a32] mb-2"
        >
          Get today&#39;s <span className="italic text-[#ff5b48]">best deals</span> in your inbox.
        </h2>
        <p className="text-[14px] text-[#3a3a38] leading-relaxed mb-5">
          A short, sharp digest of every deal worth clipping — sent every morning at 8&nbsp;AM ET.
          No spam. Unsubscribe with one tap.
        </p>

        <form onSubmit={submit} className="flex flex-col md:flex-row gap-2">
          <label htmlFor="exit-intent-email" className="sr-only">Email address</label>
          <div className="flex-1 flex items-center gap-2 border-2 border-[#2d2a32] rounded-full px-4 h-12 bg-white">
            <Mail className="w-4 h-4 shrink-0 text-[#5c5c5c]" />
            <input
              id="exit-intent-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="exit-intent-email"
              className="flex-1 bg-transparent outline-none text-sm text-[#2d2a32]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="exit-intent-submit"
            className="h-12 px-6 rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[11px] tracking-[0.22em] font-bold disabled:opacity-50"
          >
            {busy ? "SUBSCRIBING…" : "SEND ME DEALS →"}
          </button>
        </form>

        <p className="text-[11px] text-[#5c5c5c] mt-3">
          Join 5,000+ smart shoppers who never miss a deal.
        </p>
      </div>
    </div>
  );
}
